import './styles/global.css';
import './styles/pages.css';

import { route, setNotFound, mountRouter, navigate, currentPath, resolve } from './lib/router.js';
import { watchAuth, firebaseConfigured, isTauri } from './lib/firebase.js';
import { initEmbeddedSso } from './lib/embed-sso.js';
import { initAnalytics, identify as analyticsIdentify, reset as analyticsReset, trackPageview, reportError, setErrorContext } from './lib/analytics.js';
import { ensureProfile, watchProfile, stopWatchingProfile } from './lib/billing.js';

// Pages load on demand so an audience phone joining /s/ or /j/ doesn't
// download the editor, importers, and presenter.
const PAGES = {
  landing: { load: () => import('./pages/landing.js'), render: 'renderLanding' },
  auth: { load: () => import('./pages/auth.js'), render: 'renderAuth' },
  dashboard: { load: () => import('./pages/dashboard.js'), render: 'renderDashboard', dispose: 'disposeDashboard' },
  editor: { load: () => import('./pages/editor.js'), render: 'renderEditor', dispose: 'disposeEditor' },
  presenter: { load: () => import('./pages/presenter.js'), render: 'renderPresenter', dispose: 'disposePresenter' },
  session: { load: () => import('./pages/session.js'), render: 'renderSessionJoin', dispose: 'disposeSession' },
  join: { load: () => import('./pages/join.js'), render: 'renderJoin', dispose: 'disposeJoin' },
  report: { load: () => import('./pages/report.js'), render: 'renderReport', dispose: 'disposeReport' },
  print: { load: () => import('./pages/print.js'), render: 'renderPrint', dispose: 'disposePrint' },
  handout: { load: () => import('./pages/handout.js'), render: 'renderHandout', dispose: 'disposeHandout' },
  learn: { load: () => import('./pages/learn.js'), render: 'renderLearn', dispose: 'disposeLearn' },
  copy: { load: () => import('./pages/copy.js'), render: 'renderCopy', dispose: 'disposeCopy' },
  notfound: { load: () => import('./pages/notfound.js'), render: 'renderNotFound' },
};
const loadedPages = {};
let navSeq = 0;

const app = document.getElementById('app');

// Restore saved UI theme before first render to avoid flash
(function () {
  const t = localStorage.getItem('pz-theme');
  if (t && t !== 'dark') document.documentElement.dataset.theme = t;
})();

// Pin non-editor pages to dark tokens regardless of the editor theme selection.
// The editor is the only page where the user's theme choice applies.
function pinBodyTheme(dark) {
  if (dark) {
    document.body.dataset.theme = 'dark';
  } else {
    delete document.body.dataset.theme;
  }
}

initAnalytics();
registerServiceWorker();

function registerServiceWorker() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  // Only register on https / localhost — SW won't load on http (intentional).
  // Skip in Tauri to avoid the WebView2 background-fetch quirks.
  if (typeof window !== 'undefined' && (window.__TAURI__ || window.__TAURI_INTERNALS__)) return;
  // Defer until after first paint so SW install doesn't fight the main bundle.
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js').catch((err) => {
      console.warn('[sw] register failed:', err);
    });
  });
}

let currentUser = null;
let authResolved = false;

const PROTECTED = ['/dashboard', '/editor/', '/print/'];
const isProtected = (path) => PROTECTED.some((p) => path === p || path.startsWith(p));

// Disposes every loaded page except `keep` (a page re-rendering itself, e.g.
// dashboard → dashboard, manages its own teardown).
function disposeOthers(keep) {
  for (const [name, mod] of Object.entries(loadedPages)) {
    const fn = PAGES[name].dispose;
    if (name !== keep && fn) mod[fn]();
  }
}

// A deploy can remove the hashed chunk an already-open tab expects; reload once
// to pick up the new build instead of leaving a blank page.
function reloadForNewBuild(err) {
  console.warn('[router] page chunk failed to load:', err);
  reportError(err, { kind: 'chunk_load' });
  let reloaded = false;
  try { reloaded = sessionStorage.getItem('pz_chunk_reload') === '1'; } catch {}
  if (reloaded) return;
  try { sessionStorage.setItem('pz_chunk_reload', '1'); } catch {}
  window.location.reload();
}
window.addEventListener('vite:preloadError', (e) => { e.preventDefault(); reloadForNewBuild(e.payload); });

async function showPage(name, ctx, { dark = true } = {}) {
  const seq = ++navSeq;
  let mod = loadedPages[name];
  if (!mod) {
    try {
      mod = loadedPages[name] = await PAGES[name].load();
    } catch (err) {
      reloadForNewBuild(err);
      return;
    }
  }
  try { sessionStorage.removeItem('pz_chunk_reload'); } catch {}
  if (seq !== navSeq) return; // a newer navigation won while this chunk loaded
  setErrorContext({ route: name, session_code: null });
  pinBodyTheme(dark);
  disposeOthers(name);
  mod[PAGES[name].render](app, ctx);
}

route('/', (ctx) => showPage('landing', ctx));
route('/auth', (ctx) => {
  if (currentUser) { navigate('/dashboard'); return; }
  showPage('auth', ctx);
});
route('/dashboard', (ctx) => {
  if (!currentUser) { navigate('/auth'); return; }
  const cohost = !currentUser.isAnonymous && pendingCohost();
  if (cohost) { navigate(`/cohost/${cohost}`); return; }
  showPage('dashboard', { ...ctx, user: currentUser });
});
route('/editor/:deckId', (ctx) => {
  if (!currentUser) { navigate('/auth'); return; }
  showPage('editor', ctx, { dark: false });
});
route('/present/:deckId', (ctx) => {
  if (!currentUser) { navigate('/auth'); return; }
  showPage('presenter', ctx);
});
// Co-presenter invite: the presenter page shows its own sign-in gate.
route('/cohost/:sessionId', (ctx) => showPage('presenter', ctx));
route('/s/:code', (ctx) => showPage('session', ctx));
route('/screen/:link', (ctx) => showPage('session', ctx));
route('/screen/:link/:code', (ctx) => showPage('session', ctx));
route('/j/:slug', (ctx) => showPage('join', ctx));
route('/h/:code', (ctx) => showPage('handout', ctx));
route('/learn/:deckId', (ctx) => showPage('learn', ctx));
route('/copy/:token', (ctx) => showPage('copy', ctx));
route('/report/:sessionId', (ctx) => {
  if (!currentUser) { navigate('/auth'); return; }
  showPage('report', ctx);
});
route('/print/:deckId', (ctx) => {
  if (!currentUser) { navigate('/auth'); return; }
  showPage('print', ctx);
});
setNotFound((ctx) => showPage('notfound', ctx));

mountRouter();

// If embedded in AKUTracker, ask the parent for an SSO token and sign in.
// Fires early so a framed, unsigned instance authenticates before the user
// would otherwise see the login screen. No-op when top-level or in Tauri.
initEmbeddedSso();

if (isTauri()) {
  import('./lib/desktop-update.js').then((m) => m.scheduleDesktopUpdateCheck()).catch(() => {});
}

// A co-presenter invite opened before signing in (or while signed in as an
// anonymous participant) resumes once a real account is signed in. Sign-in
// fires more than one navigate('/dashboard'), so the marker stays until the
// presenter page has loaded the session and clears it.
function pendingCohost() {
  try { return sessionStorage.getItem('pz_cohost_pending'); } catch { return null; }
}

watchAuth((user) => {
  const wasSignedIn = Boolean(currentUser);
  const wasAnonymous = Boolean(currentUser?.isAnonymous);
  currentUser = user;
  const path = currentPath();

  // Analytics: identify on sign-in, reset on sign-out.
  // Billing: ensure a free profile exists and keep Pro status live in-session.
  if (user && !wasSignedIn) {
    analyticsIdentify(user.uid, {
      email: user.email || undefined,
      name: user.displayName || undefined,
      provider: user.providerData?.[0]?.providerId || undefined,
    });
    // Skip anonymous audience joiners — only real (non-anon) accounts get a
    // billing profile / Pro status.
    if (!user.isAnonymous) {
      ensureProfile(user);
      watchProfile(user.uid);
    }
  } else if (!user && wasSignedIn) {
    analyticsReset();
    stopWatchingProfile();
  }

  if (!authResolved) {
    authResolved = true;
    resolve();
    return;
  }

  if (user && !user.isAnonymous && (!wasSignedIn || wasAnonymous)) {
    const cohost = pendingCohost();
    if (cohost) { navigate(`/cohost/${cohost}`); return; }
  }

  if (user && !wasSignedIn && (path === '/auth' || path === '/')) {
    // Resume a pending share-a-copy import if the user signed in via /copy.
    let pendingCopy = null;
    try { pendingCopy = sessionStorage.getItem('pz_copy_token'); } catch {}
    if (pendingCopy) {
      try { sessionStorage.removeItem('pz_copy_token'); } catch {}
      navigate(`/copy/${pendingCopy}`);
    } else {
      navigate('/dashboard');
    }
  } else if (!user && wasSignedIn) {
    navigate('/');
  } else {
    resolve();
  }
});

if (!firebaseConfigured) {
  authResolved = true;
  resolve();
}

setTimeout(() => {
  if (!authResolved) {
    authResolved = true;
    resolve();
  }
}, 1500);
